import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertTriangle, Wallet, ShieldCheck, CheckCircle } from 'lucide-react';

interface PayPalButtonsProps {
  amount: number;
  onSuccess: (details: any) => void;
  onError: (error: any) => void;
  description: string;
}

const PayPalButtons: React.FC<PayPalButtonsProps> = ({ amount, onSuccess, onError, description }) => {
  const paypalRef = useRef<HTMLDivElement>(null);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [loadingScript, setLoadingScript] = useState(false);
  const isTrial = amount === 0;

  useEffect(() => {
    let isMounted = true;
    const CLIENT_ID = 'AUSTr9m9C00WctgEYqdVIkaPj2k1LzDwYEpEVe38XtfFyYwy8BXR0xKkBtAt3T8nUw3cHUk-YDWm0128';

    const loadPaypalScript = () => {
      if (isTrial) {
        setIsSdkLoaded(true);
        return;
      }

      if ((window as any).paypal) {
        setIsSdkLoaded(true);
        setTimeout(() => {
          if (isMounted) renderButtons();
        }, 100);
        return;
      }

      if (loadingScript) return;
      setLoadingScript(true);

      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}&currency=USD`;
      script.setAttribute('data-sdk-integration-source', 'button-factory');
      script.crossOrigin = 'anonymous';
      script.async = true;

      script.onload = () => {
        if (!isMounted) return;
        setIsSdkLoaded(true);
        setTimeout(() => {
          if (isMounted) renderButtons();
        }, 100);
      };

      script.onerror = (err) => {
        console.error('PayPal Script Load Error:', err);
        if (isMounted) setSdkError("Celestial Treasury script blocked or failed to load.");
      };

      try {
        document.head.appendChild(script);
      } catch (e) {
        console.error('PayPal Script Injection Error:', e);
        if (isMounted) setSdkError("Failed to inject payment gateway into the current realm.");
      }
    };

    const renderButtons = () => {
      const paypal = (window as any).paypal;
      if (!paypal || !paypalRef.current || !isMounted || isTrial) return;

      try {
        paypalRef.current.innerHTML = '';
        paypal.Buttons({
          style: {
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'pay',
          },
          createOrder: (data: any, actions: any) => {
            return actions.order.create({
              purchase_units: [
                {
                  description: description,
                  amount: {
                    currency_code: 'USD',
                    value: amount.toFixed(2),
                  },
                },
              ],
            });
          },
          onApprove: async (data: any, actions: any) => {
            try {
              const details = await actions.order.capture();
              onSuccess(details);
            } catch (err) {
              console.error('PayPal Capture Error:', err);
              onError(err);
            }
          },
          onError: (err: any) => {
            console.error('PayPal Buttons Error:', err);
            if (String(err).includes('window host')) {
              setSdkError("The payment provider cannot verify your host in this restricted environment.");
            } else {
              onError(err);
            }
          },
        }).render(paypalRef.current).catch((err: any) => {
          console.error('PayPal Render Promise Error:', err);
          if (isMounted) setSdkError("The payment interface failed to manifest.");
        });
      } catch (e: any) {
        console.error("PayPal UI Exception:", e);
        if (isMounted) setSdkError("Alignment issue with the payment provider interface.");
      }
    };

    loadPaypalScript();

    return () => {
      isMounted = false;
    };
  }, [amount, description, isTrial]);

  const handleMockPayment = () => {
    onSuccess({
      id: (isTrial ? 'TRIAL-AUTH-' : 'SECURE-TRANS-') + Math.random().toString(36).substr(2, 9).toUpperCase(),
      purchase_units: [{ amount: { value: amount.toFixed(2) } }]
    });
  };

  if (isTrial) {
    return (
      <div className="w-full space-y-4 animate-fade-in">
        <div className="p-8 bg-slate-950 border border-indigo-900/50 rounded-sm text-center">
          <div className="w-16 h-16 bg-indigo-600/20 text-indigo-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldCheck size={32} />
          </div>
          <h4 className="text-white font-serif text-lg mb-2 uppercase tracking-widest">Trial Authorization</h4>
          <p className="text-indigo-300/70 text-xs leading-relaxed mb-8 italic">
            "Your registry access will be granted for 72 hours at no cost. Allie will follow up via the provided voice line to secure the vault connection."
          </p>
          <button 
            onClick={handleMockPayment}
            className="w-full py-5 bg-amber-500 text-slate-950 font-bold uppercase tracking-[0.2em] text-[10px] rounded-sm hover:bg-amber-400 transition-all shadow-xl flex items-center justify-center gap-3"
          >
            Authorize My Free Trial <CheckCircle size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div ref={paypalRef} className={`min-h-[150px] flex flex-col items-center justify-center transition-all ${sdkError ? 'hidden' : ''}`}>
        {!isSdkLoaded && !sdkError && (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="animate-spin text-amber-500" size={24} />
            <p className="text-[10px] text-indigo-400 uppercase tracking-widest">Awaiting Gateway...</p>
          </div>
        )}
      </div>

      {sdkError && (
        <div className="p-6 bg-slate-900 border border-amber-500/20 rounded-sm text-center space-y-4 animate-fade-in">
          <div className="flex justify-center">
            <AlertTriangle className="text-amber-500" size={32} />
          </div>
          <div className="space-y-2">
            <p className="text-xs text-amber-200 uppercase tracking-widest font-bold">Divine Connection Restricted</p>
            <p className="text-[10px] text-indigo-400 leading-relaxed">
              Your environment restricts the PayPal treasury. For this preview, use manual verification.
            </p>
          </div>
          
          <button 
            onClick={handleMockPayment}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold uppercase tracking-widest text-[10px] rounded-sm transition-all flex items-center justify-center gap-2"
          >
            <Wallet size={14} /> Perform Manual Verification
          </button>
        </div>
      )}
    </div>
  );
};

export default PayPalButtons;